import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth, signInWithGoogle } from "@/lib/auth";
import BackToSiteBadge from "@/components/site/BackToSiteBadge";

const QR_API = "https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=12&data=";

export default function AffiliateDashboard() {
  const { user, affiliate, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useSearchParams();
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [celebrateToast, setCelebrateToast] = useState(false);
  const navigate = useNavigate();

  const fetchDash = async () => {
    try {
      const r = await axios.get(`${API}/affiliate/dashboard`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setData({ not_enrolled: true });
      else setErr(e?.response?.data?.detail || "Could not load dashboard");
    }
  };

  useEffect(() => {
    if (!user && !loading) return;
    if (user) fetchDash();
  }, [user, loading]);

  // Poll for 60s after enrollment success flag
  useEffect(() => {
    if (search.get("enrollment") !== "success") return;
    const t0 = Date.now();
    const iv = setInterval(() => {
      fetchDash();
      if (Date.now() - t0 > 60_000) clearInterval(iv);
    }, 3000);
    return () => clearInterval(iv);
  }, [search]);

  // Open the first-share nudge as soon as the dashboard loads with ?enrollment=success
  // AND the affiliate is active (so we have a referral URL to share).
  useEffect(() => {
    if (search.get("enrollment") !== "success") return;
    if (data?.affiliate?.status === "active" && data?.referral_url) {
      setWelcomeOpen(true);
      // Strip the ?enrollment flag so refresh doesn't reopen the modal
      const next = new URLSearchParams(search);
      next.delete("enrollment");
      setSearch(next, { replace: true });
    }
  }, [data?.affiliate?.status, data?.referral_url]);

  if (loading) return <Splash />;
  if (!user) {
    return (
      <Shell>
        <h1 className="font-display text-3xl mb-4">Affiliate dashboard</h1>
        <p className="text-[var(--muted)] mb-6">Sign in to see your referral stats and balances.</p>
        <button onClick={() => signInWithGoogle("/affiliate/dashboard")} className="px-6 py-3 rounded-full bg-[var(--marigold)] text-white font-medium" data-testid="dash-signin">
          Continue with Google
        </button>
      </Shell>
    );
  }

  if (data?.not_enrolled || !affiliate) {
    return (
      <Shell>
        <h1 className="font-display text-3xl mb-4">You're not enrolled yet</h1>
        <p className="text-[var(--muted)] mb-6">Pay the one-time $2.99 enrollment to activate your affiliate account.</p>
        <Link to="/affiliate/signup" className="px-6 py-3 rounded-full bg-[var(--marigold)] text-white font-medium" data-testid="dash-goto-signup">
          Finish enrollment
        </Link>
      </Shell>
    );
  }

  if (err) {
    return (
      <Shell>
        <h1 className="font-display text-3xl mb-4">Something went wrong</h1>
        <p className="text-red-600 mb-6">{err}</p>
      </Shell>
    );
  }

  if (!data) return <Splash />;

  const aff = data.affiliate;
  const bal = data.balances;
  const m = data.metrics;
  const refUrl = data.referral_url;

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <header className="px-6 md:px-10 py-5 border-b border-[rgba(15,31,23,0.08)] flex items-center justify-between">
        <BackToSiteBadge />
        <div className="flex items-center gap-3 text-sm">
          <span className="text-[var(--muted)] hidden md:inline">{user.email}</span>
          <button onClick={() => logout().then(() => navigate("/"))} className="px-4 py-2 rounded-full border border-[rgba(15,31,23,0.15)] text-sm" data-testid="dash-logout">
            Sign out
          </button>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10 max-w-6xl mx-auto space-y-10">
        {/* Status + status badge */}
        <section>
          <div className="flex items-center gap-3 mb-2">
            <span className={`inline-block w-2 h-2 rounded-full ${aff.status === "active" ? "bg-[var(--jade)]" : aff.status === "suspended" ? "bg-red-500" : "bg-[var(--marigold)]"}`} />
            <span className="font-mono-caption tracking-[0.2em] uppercase text-xs text-[var(--muted)]" data-testid="dash-status">
              {aff.status === "active" ? "Active" : aff.status === "pending_payment" ? "Pending payment" : aff.status}
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl mb-2">Welcome back, {aff.name?.split(" ")[0] || "Partner"}.</h1>
          <p className="text-[var(--muted)]">Your code · <strong className="text-[var(--ink)]">{aff.affiliate_id}</strong></p>
        </section>

        {aff.status !== "active" && (
          <div className="p-5 rounded-2xl bg-[var(--marigold)]/10 border border-[var(--marigold)]/30">
            <h3 className="font-display text-lg mb-2">Finish enrollment to start earning</h3>
            <p className="text-sm text-[var(--muted)] mb-4">Your account activates automatically after our server confirms the $2.99 payment. If you've already paid, this page will update within a minute.</p>
            <Link to="/affiliate/signup" className="inline-block px-5 py-2.5 rounded-full bg-[var(--marigold)] text-white text-sm font-medium" data-testid="dash-pay-enrollment">Pay enrollment</Link>
          </div>
        )}

        {/* Metrics */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Clicks (all time)" value={m.total_clicks} testid="metric-clicks-total" />
          <Stat label="Clicks (30d)" value={m.recent_clicks_30d} testid="metric-clicks-30d" />
          <Stat label="Attributed orders" value={m.attributed_orders} testid="metric-orders" />
          <Stat label="Conversion rate" value={`${m.conversion_rate_pct}%`} note="orders ÷ clicks" testid="metric-conversion" />
        </section>

        {/* Balances */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Balance label="Pending" cents={bal.pending} tone="muted" note={`Holds ${data.settings.refund_hold_days}d`} testid="bal-pending" />
          <Balance label="Available" cents={bal.available} tone="jade" note={`Payout ≥ $${(data.settings.min_payout_cents / 100).toFixed(0)}`} testid="bal-available" />
          <Balance label="Reserved (payout)" cents={bal.reserved} tone="marigold" testid="bal-reserved" />
          <Balance label="Paid out" cents={bal.paid} tone="muted" testid="bal-paid" />
        </section>

        {/* Referral toolkit */}
        {aff.status === "active" && refUrl && (
          <section className="p-6 rounded-2xl bg-white/70 border border-[rgba(15,31,23,0.08)]">
            <h2 className="font-display text-2xl mb-4">Your referral toolkit</h2>
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div className="space-y-4">
                <CopyRow label="Referral link" value={refUrl} testid="copy-ref-link" />
                <CopyRow label="Referral code" value={aff.affiliate_id} testid="copy-ref-code" />
                <div className="flex flex-wrap gap-3">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`Grab the From Milk to Income ebook for $3 — use my link: ${refUrl}`)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jade)] text-white text-sm font-medium"
                    data-testid="share-whatsapp"
                  >
                    Share on WhatsApp
                  </a>
                  <a
                    href={`${QR_API}${encodeURIComponent(refUrl)}&format=png&download=1`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(15,31,23,0.15)] text-sm"
                    data-testid="download-qr"
                  >
                    Download QR
                  </a>
                  <a
                    href={`${API}/affiliate/export.csv`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(15,31,23,0.15)] text-sm"
                    data-testid="export-csv"
                  >
                    Export CSV
                  </a>
                </div>
              </div>
              <div className="justify-self-center">
                <img
                  alt="Referral QR code"
                  src={`${QR_API}${encodeURIComponent(refUrl)}`}
                  width={160}
                  height={160}
                  className="rounded-xl border border-[rgba(15,31,23,0.1)] bg-white"
                  data-testid="qr-image"
                />
              </div>
            </div>
          </section>
        )}

        {/* Personal copy — the ebook watermarked with their affiliate code */}
        {aff.status === "active" && <PersonalCopyCard />}

        {/* Ledger */}
        <section>
          <h2 className="font-display text-2xl mb-4">Recent activity</h2>
          {data.recent_entries.length === 0 ? (
            <div className="p-6 rounded-xl bg-white/50 text-[var(--muted)] text-sm">No commission activity yet. Once someone buys through your link, you'll see it here.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase border-b border-[rgba(15,31,23,0.08)]">
                    <th className="py-3 pr-3">Date</th>
                    <th className="py-3 pr-3">Type</th>
                    <th className="py-3 pr-3">Order</th>
                    <th className="py-3 pr-3">Status</th>
                    <th className="py-3 pr-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody data-testid="ledger-rows">
                  {data.recent_entries.map((e) => (
                    <tr key={e.entry_id} className="border-b border-[rgba(15,31,23,0.05)]">
                      <td className="py-3 pr-3 whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</td>
                      <td className="py-3 pr-3 capitalize">{e.type.replace("_", " ")}</td>
                      <td className="py-3 pr-3 font-mono text-xs">{e.order_id || "—"}</td>
                      <td className="py-3 pr-3"><StatusPill s={e.status} /></td>
                      <td className={`py-3 pr-3 text-right font-mono ${e.amount_cents < 0 ? "text-red-600" : ""}`}>${(e.amount_cents / 100).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Payout profile summary (masked) + inline editor */}
        <section>
          <h2 className="font-display text-2xl mb-4">Payout profile</h2>
          <PayoutEditor aff={aff} onSaved={fetchDash} onFirstSetup={() => setCelebrateToast(true)} />
        </section>

        {/* Payouts list */}
        {data.payouts?.length > 0 && (
          <section>
            <h2 className="font-display text-2xl mb-4">Payout history</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase border-b border-[rgba(15,31,23,0.08)]">
                  <th className="py-3 pr-3">Paid on</th>
                  <th className="py-3 pr-3">Batch</th>
                  <th className="py-3 pr-3">Reference</th>
                  <th className="py-3 pr-3 text-right">USD</th>
                  <th className="py-3 pr-3 text-right">TZS (if recorded)</th>
                </tr>
              </thead>
              <tbody>
                {data.payouts.map((p) => (
                  <tr key={p.item_id} className="border-b border-[rgba(15,31,23,0.05)]">
                    <td className="py-3 pr-3 whitespace-nowrap">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}</td>
                    <td className="py-3 pr-3 font-mono text-xs">{p.batch_id}</td>
                    <td className="py-3 pr-3">{p.external_reference || "—"}</td>
                    <td className="py-3 pr-3 text-right font-mono">${(p.amount_cents / 100).toFixed(2)}</td>
                    <td className="py-3 pr-3 text-right font-mono">{p.amount_tzs_cents_recorded ? `TZS ${(p.amount_tzs_cents_recorded / 100).toLocaleString()}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </main>

      {welcomeOpen && refUrl && (
        <WelcomeShareNudge
          referralUrl={refUrl}
          firstName={aff.name?.split(" ")[0] || "Partner"}
          onClose={() => setWelcomeOpen(false)}
        />
      )}

      {celebrateToast && refUrl && (
        <PayoutCelebrationToast
          referralUrl={refUrl}
          onClose={() => setCelebrateToast(false)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, note, testid }) {
  return (
    <div className="p-4 rounded-2xl bg-white/70 border border-[rgba(15,31,23,0.08)]">
      <div className="text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)]">{label}</div>
      <div className="font-display text-2xl md:text-3xl mt-1" data-testid={testid}>{value}</div>
      {note && <div className="text-xs text-[var(--muted)] mt-1">{note}</div>}
    </div>
  );
}

function Balance({ label, cents, tone, note, testid }) {
  const tones = { jade: "text-[var(--jade)]", marigold: "text-[var(--marigold)]", muted: "text-[var(--ink)]" };
  return (
    <div className="p-4 rounded-2xl bg-white/70 border border-[rgba(15,31,23,0.08)]">
      <div className="text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)]">{label}</div>
      <div className={`font-display text-2xl md:text-3xl mt-1 ${tones[tone] || ""}`} data-testid={testid}>${(cents / 100).toFixed(2)}</div>
      {note && <div className="text-xs text-[var(--muted)] mt-1">{note}</div>}
    </div>
  );
}

function CopyRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] mb-2">{label}</div>
      <div className="flex items-stretch gap-2">
        <input readOnly value={value} className="flex-1 input-base font-mono text-sm" />
        <button
          onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="px-4 rounded-xl bg-[var(--ink)] text-[var(--cream)] text-sm"
          data-testid={testid}
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    pending: "bg-[var(--marigold)]/15 text-[var(--marigold)]",
    available: "bg-[var(--jade)]/15 text-[var(--jade)]",
    reserved: "bg-blue-100 text-blue-800",
    paid: "bg-gray-200 text-gray-700",
    reversed: "bg-red-100 text-red-700",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-mono-caption tracking-[0.1em] uppercase ${map[s] || "bg-gray-100 text-gray-700"}`}>{s}</span>;
}

function Splash() {
  return <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-4 border-[var(--marigold)] border-t-transparent animate-spin" /></div>;
}

function Shell({ children }) {
  return <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)] px-6 py-16 flex items-center justify-center"><div className="max-w-md">{children}</div></div>;
}


function PersonalCopyCard() {
  const [state, setState] = useState({ loading: true, url: null, err: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/affiliate/me/personal-copy`, { withCredentials: true });
        if (!alive) return;
        setState({ loading: false, url: r.data.download_url, err: null });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, url: null, err: e?.response?.data?.detail || "Could not load your copy yet." });
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <section className="p-6 rounded-2xl bg-[var(--ink)] text-[var(--cream)] relative overflow-hidden" data-testid="personal-copy-card">
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[var(--marigold)]/25 blur-3xl pointer-events-none" aria-hidden />
      <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-5 items-center">
        <div>
          <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--marigold-2)] mb-2">
            Your personal copy
          </div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight mb-2">
            Keep this close — it's yours, watermarked.
          </h2>
          <p className="text-sm text-[var(--cream)]/70 max-w-md">
            Every page of your copy carries your affiliate code. Read it, learn the pitch, share the link with confidence.
          </p>
        </div>
        <div>
          {state.loading && <div className="text-sm text-[var(--cream)]/60">Loading…</div>}
          {state.err && !state.loading && (
            <div className="text-sm text-[var(--marigold-2)]" data-testid="personal-copy-err">{state.err}</div>
          )}
          {state.url && (
            <a
              href={state.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--marigold)] text-white font-medium hover:bg-[var(--marigold)]/90 transition"
              data-testid="personal-copy-download"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download PDF
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

const MOBILE_PROVIDERS = ["M-Pesa", "Airtel Money", "Tigo Pesa", "Halo Pesa", "T-Pesa", "Other"];

function PayoutEditor({ aff, onSaved, onFirstSetup }) {
  const configured = aff.payout_method && aff.payout_method !== "pending";
  const hasPending = !!aff.payout_pending_masked;
  const [editing, setEditing] = useState(!configured && !hasPending);
  const [method, setMethod] = useState(configured && aff.payout_method === "bank" ? "bank" : "mobile_money");
  const [form, setForm] = useState({
    mobile_provider: aff.payout_profile_masked?.mobile_provider || "M-Pesa",
    mobile_number: "",
    bank_name: aff.payout_profile_masked?.bank_name || "",
    bank_account: "",
    bank_holder: aff.payout_profile_masked?.bank_holder || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [verifyInfo, setVerifyInfo] = useState(null); // { sent_to } after submit

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/affiliate/me/payout-history`, { withCredentials: true });
      setHistory(r.data.entries || []);
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    if (historyOpen && history === null) loadHistory();
  }, [historyOpen]);

  const save = async () => {
    setErr(null);
    setSaving(true);
    try {
      const payload = {
        payout_method: method,
        payout_mobile_provider: method === "mobile_money" ? form.mobile_provider : null,
        payout_mobile_number: method === "mobile_money" ? form.mobile_number.trim() : null,
        payout_bank_name: method === "bank" ? form.bank_name.trim() : null,
        payout_bank_account: method === "bank" ? form.bank_account.trim() : null,
        payout_bank_holder: method === "bank" ? form.bank_holder.trim() : null,
      };
      const resp = await axios.post(`${API}/affiliate/me/payout`, payload, { withCredentials: true });
      setEditing(false);
      // Refresh audit log if it was already open
      if (historyOpen) await loadHistory();
      else setHistory(null); // invalidate so next open re-fetches
      if (resp.data?.verification_required) {
        setVerifyInfo({ sent_to: resp.data.sent_to });
      }
      if (resp.data?.was_first_setup) onFirstSetup?.();
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save payout details.");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="space-y-3" data-testid="payout-configured-wrap">
        {hasPending && (
          <PayoutVerificationCard
            aff={aff}
            justSentTo={verifyInfo?.sent_to}
            onDone={() => { setVerifyInfo(null); onSaved?.(); }}
          />
        )}
        <div className="p-5 rounded-xl bg-white/60 border border-[rgba(15,31,23,0.08)] text-sm space-y-1" data-testid="payout-configured">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <div><span className="text-[var(--muted)]">Method:</span> {aff.payout_method === "mobile_money" ? "Mobile money" : "Bank transfer"}</div>
              {aff.payout_method === "mobile_money" ? (
                <>
                  <div><span className="text-[var(--muted)]">Provider:</span> {aff.payout_profile_masked?.mobile_provider}</div>
                  <div><span className="text-[var(--muted)]">Number:</span> {aff.payout_profile_masked?.mobile_number}</div>
                </>
              ) : (
                <>
                  <div><span className="text-[var(--muted)]">Bank:</span> {aff.payout_profile_masked?.bank_name}</div>
                  <div><span className="text-[var(--muted)]">Account:</span> {aff.payout_profile_masked?.bank_account}</div>
                  <div><span className="text-[var(--muted)]">Holder:</span> {aff.payout_profile_masked?.bank_holder}</div>
                </>
              )}
            </div>
            <button onClick={() => setEditing(true)} className="text-sm underline font-medium" data-testid="payout-edit-btn">Edit</button>
          </div>
          <p className="pt-3 text-xs text-[var(--muted)]">Changes to payout details are logged and require re-verification before the next payout.</p>
        </div>

        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] hover:text-[var(--ink)]"
          data-testid="payout-history-toggle"
        >
          {historyOpen ? "▾ Hide change history" : "▸ Show change history"}
        </button>

        {historyOpen && (
          <div className="rounded-xl bg-white/40 border border-[rgba(15,31,23,0.06)] p-4" data-testid="payout-history-list">
            {history === null ? (
              <div className="text-sm text-[var(--muted)]">Loading…</div>
            ) : history.length === 0 ? (
              <div className="text-sm text-[var(--muted)]">No changes recorded yet.</div>
            ) : (
              <ol className="relative border-l border-[rgba(15,31,23,0.12)] ml-2 space-y-4">
                {history.map((h, i) => <PayoutHistoryItem key={i} entry={h} />)}
              </ol>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-5 rounded-xl bg-white/80 border border-[rgba(15,31,23,0.08)] space-y-4" data-testid="payout-editor">
      {!configured && (
        <div className="text-sm text-[var(--muted)]">
          Choose where to receive your monthly payouts. You can change this later.
        </div>
      )}
      <div className="flex gap-2 text-sm">
        <button
          type="button"
          onClick={() => setMethod("mobile_money")}
          className={`px-4 py-2 rounded-full border ${method === "mobile_money" ? "bg-[var(--ink)] text-[var(--cream)] border-[var(--ink)]" : "border-[rgba(15,31,23,0.15)]"}`}
          data-testid="payout-tab-mobile"
        >
          Mobile money
        </button>
        <button
          type="button"
          onClick={() => setMethod("bank")}
          className={`px-4 py-2 rounded-full border ${method === "bank" ? "bg-[var(--ink)] text-[var(--cream)] border-[var(--ink)]" : "border-[rgba(15,31,23,0.15)]"}`}
          data-testid="payout-tab-bank"
        >
          Bank transfer
        </button>
      </div>

      {method === "mobile_money" ? (
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="block mb-1 text-[var(--muted)]">Provider</span>
            <select
              value={form.mobile_provider}
              onChange={(e) => setForm({ ...form, mobile_provider: e.target.value })}
              className="input-base"
              data-testid="payout-mobile-provider"
            >
              {MOBILE_PROVIDERS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="block mb-1 text-[var(--muted)]">Mobile number (with country code)</span>
            <input
              type="tel"
              value={form.mobile_number}
              onChange={(e) => setForm({ ...form, mobile_number: e.target.value })}
              placeholder="+255 712 345 678"
              className="input-base"
              data-testid="payout-mobile-number"
            />
          </label>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="block mb-1 text-[var(--muted)]">Bank name</span>
            <input
              value={form.bank_name}
              onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
              placeholder="e.g. NMB Bank"
              className="input-base"
              data-testid="payout-bank-name"
            />
          </label>
          <label className="block text-sm">
            <span className="block mb-1 text-[var(--muted)]">Account number</span>
            <input
              value={form.bank_account}
              onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
              className="input-base"
              data-testid="payout-bank-account"
            />
          </label>
          <label className="block text-sm md:col-span-2">
            <span className="block mb-1 text-[var(--muted)]">Account holder (as it appears on the account)</span>
            <input
              value={form.bank_holder}
              onChange={(e) => setForm({ ...form, bank_holder: e.target.value })}
              className="input-base"
              data-testid="payout-bank-holder"
            />
          </label>
        </div>
      )}

      {err && <div className="text-sm text-red-700" data-testid="payout-error">{err}</div>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-5 py-2.5 rounded-full bg-[var(--marigold)] text-white text-sm font-medium disabled:opacity-60"
          data-testid="payout-save"
        >
          {saving ? "Saving…" : "Save payout details"}
        </button>
        {configured && (
          <button
            type="button"
            onClick={() => { setEditing(false); setErr(null); }}
            className="text-sm underline text-[var(--muted)]"
            data-testid="payout-cancel"
          >
            Cancel
          </button>
        )}
      </div>
      <p className="text-xs text-[var(--muted)]">We only use these details to send your commission. They're masked on your dashboard after saving.</p>
    </div>
  );
}

function PayoutHistoryItem({ entry }) {
  const dt = entry.created_at ? new Date(entry.created_at) : null;
  const before = entry.before || {};
  const after = entry.after || {};
  const fmt = (snap) => {
    if (!snap?.payout_method || snap.payout_method === "pending") return "— not configured —";
    if (snap.payout_method === "mobile_money") {
      return `${snap.payout_mobile_provider || "Mobile"} · ${snap.payout_mobile_number_tail || "—"}`;
    }
    return `${snap.payout_bank_name || "Bank"} · ${snap.payout_bank_account_tail || "—"}`;
  };
  const isFirst = !before?.payout_method || before.payout_method === "pending";
  return (
    <li className="ml-4">
      <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-[var(--marigold)] mt-1.5" aria-hidden />
      <div className="text-xs text-[var(--muted)] font-mono-caption tracking-[0.15em] uppercase">
        {dt ? dt.toLocaleString() : ""}
      </div>
      <div className="text-sm">
        {isFirst ? (
          <>Set payout to <strong>{fmt(after)}</strong></>
        ) : (
          <>
            Changed from <span className="text-[var(--muted)]">{fmt(before)}</span>{" → "}
            <strong>{fmt(after)}</strong>
          </>
        )}
      </div>
    </li>
  );
}

function WelcomeShareNudge({ referralUrl, firstName, onClose }) {
  const [remaining, setRemaining] = useState(60);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const waMsg = `I just joined Milk2Income as an affiliate 🧡 Grab the "From Milk to Income" ebook for $3 — use my link: ${referralUrl}`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(waMsg)}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(referralUrl); } catch { /* noop */ }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-[rgba(10,20,15,0.78)] backdrop-blur-sm flex items-center justify-center px-4" data-testid="welcome-nudge-overlay">
      <div className="w-full max-w-lg bg-[var(--cream)] rounded-3xl p-6 md:p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[var(--marigold)]/25 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">🎉</span>
            <span className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--marigold)]">You're in</span>
          </div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight mb-2">
            Welcome aboard, {firstName}.
          </h3>
          <p className="text-sm text-[var(--muted)] mb-5">
            One quick win before you close this tab — share your link with a dairy friend on WhatsApp. The first share is the hardest; after that it's downhill.
          </p>

          <div className="mb-4 p-3 rounded-xl bg-white/70 border border-[rgba(15,31,23,0.08)]">
            <div className="text-[10px] font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] mb-1">Your referral link</div>
            <div className="font-mono text-xs break-all">{referralUrl}</div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="px-5 py-3 rounded-full border border-[rgba(15,31,23,0.15)] text-sm text-[var(--muted)]"
              data-testid="welcome-nudge-close"
            >
              Maybe later
            </button>
            <button
              onClick={copyLink}
              className="px-5 py-3 rounded-full border border-[rgba(15,31,23,0.15)] text-sm"
              data-testid="welcome-nudge-copy"
            >
              Copy link
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              className="flex-1 px-5 py-3 rounded-full bg-[var(--jade)] text-white text-sm font-medium text-center inline-flex items-center justify-center gap-2"
              data-testid="welcome-nudge-share-whatsapp"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04 0C5.46 0 .09 5.37.09 11.96c0 2.11.55 4.17 1.6 5.98L0 24l6.2-1.63a11.9 11.9 0 0 0 5.84 1.49h.01c6.58 0 11.95-5.37 11.95-11.95 0-3.19-1.24-6.19-3.48-8.43zM12.04 21.9a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.68.96.98-3.58-.23-.37a9.88 9.88 0 0 1-1.52-5.31c0-5.48 4.46-9.94 9.95-9.94 2.66 0 5.16 1.04 7.04 2.92a9.88 9.88 0 0 1 2.92 7.03c0 5.48-4.46 9.95-9.95 9.95zm5.46-7.44c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.08-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.79-1.67-2.09-.17-.3-.02-.46.13-.6.14-.14.3-.37.45-.55.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.12 3.24 5.14 4.54.72.31 1.28.5 1.72.64.72.23 1.37.2 1.89.12.58-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z"/>
              </svg>
              Share on WhatsApp
            </a>
          </div>

          <div className="mt-4 text-[11px] font-mono-caption tracking-[0.15em] uppercase text-[var(--muted)] text-center">
            This nudge will self-close in {remaining}s · {remaining === 0 ? "closing…" : "no rush"}
          </div>
        </div>
      </div>
    </div>
  );
}

function PayoutCelebrationToast({ referralUrl, onClose }) {
  const inputRef = useRef(null);

  useEffect(() => {
    // Pre-select the referral link so one Cmd+C grabs it
    const t = setTimeout(() => {
      try { inputRef.current?.focus(); inputRef.current?.select(); } catch { /* noop */ }
    }, 60);
    const auto = setTimeout(onClose, 15_000);
    return () => { clearTimeout(t); clearTimeout(auto); };
  }, [onClose]);

  const copy = () => {
    try { navigator.clipboard.writeText(referralUrl); } catch { /* noop */ }
  };

  return (
    <div className="fixed bottom-6 inset-x-4 sm:inset-x-auto sm:right-6 z-[65] sm:max-w-md animate-[slide-up_0.3s_ease-out]" data-testid="payout-celebration-toast">
      <div className="rounded-2xl bg-[var(--jade)] text-white p-5 shadow-2xl relative overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 opacity-80 hover:opacity-100"
          data-testid="payout-celebration-close"
          aria-label="Dismiss"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase opacity-80 mb-1">🎊 Payout saved</div>
        <h4 className="font-display text-xl leading-tight mb-1">You're ready for your first commission.</h4>
        <p className="text-sm opacity-90 mb-3">
          Your link is already selected below. Hit Copy, send it to one person today.
        </p>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            readOnly
            value={referralUrl}
            className="flex-1 rounded-xl px-3 py-2 text-sm text-[var(--ink)] bg-white font-mono"
            data-testid="payout-celebration-link"
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={copy}
            className="px-4 rounded-xl bg-white text-[var(--jade)] font-medium text-sm"
            data-testid="payout-celebration-copy"
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  );
}


function PayoutVerificationCard({ aff, justSentTo, onDone }) {
  const pending = aff.payout_pending_masked || {};
  const emailMasked = aff.payout_verification_email_masked || justSentTo || "your email";
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendOk, setResendOk] = useState(false);

  const label = pending.payout_method === "bank"
    ? `${pending.bank_name || "Bank"} · ${pending.bank_account || "—"} (${pending.bank_holder || ""})`
    : `${pending.mobile_provider || "Mobile money"} · ${pending.mobile_number || "—"}`;

  const verify = async () => {
    const clean = code.replace(/\s+/g, "").trim();
    if (clean.length < 4) { setErr("Enter the 6-digit code from your email."); return; }
    setErr(null);
    setBusy(true);
    try {
      await axios.post(`${API}/affiliate/me/payout/verify`, { code: clean }, { withCredentials: true });
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not verify code.");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setErr(null);
    setResendOk(false);
    setResendBusy(true);
    try {
      await axios.post(`${API}/affiliate/me/payout/resend`, {}, { withCredentials: true });
      setResendOk(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not resend code.");
    } finally {
      setResendBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Discard the pending payout change?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/affiliate/me/payout/cancel`, {}, { withCredentials: true });
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not cancel.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-5 rounded-xl bg-[var(--marigold)]/10 border border-[var(--marigold)]/35" data-testid="payout-verify-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-[var(--marigold)] text-white flex items-center justify-center text-sm font-bold shrink-0">!</div>
        <div className="flex-1">
          <div className="font-medium text-[var(--ink)]">Confirm your new payout destination</div>
          <p className="text-sm text-[var(--muted)] mt-1">
            We emailed a 6-digit code to <strong className="text-[var(--ink)]">{emailMasked}</strong>. Enter it below to switch payouts to <strong className="text-[var(--ink)]">{label}</strong>. Until you confirm, your previous verified destination stays active.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-2">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ""))}
          className="input-base flex-1 tracking-[0.4em] font-mono text-center"
          data-testid="payout-verify-code"
        />
        <button
          type="button"
          onClick={verify}
          disabled={busy || code.length < 4}
          className="px-5 py-2.5 rounded-full bg-[var(--marigold)] text-white text-sm font-medium disabled:opacity-60"
          data-testid="payout-verify-submit"
        >
          {busy ? "Verifying…" : "Confirm change"}
        </button>
      </div>

      {err && <div className="text-xs text-red-700 mt-1" data-testid="payout-verify-error">{err}</div>}
      {resendOk && <div className="text-xs text-[var(--jade)] mt-1">New code sent — check your inbox.</div>}

      <div className="flex items-center gap-3 mt-3 text-xs">
        <button
          type="button"
          onClick={resend}
          disabled={resendBusy}
          className="underline text-[var(--muted)] hover:text-[var(--ink)] disabled:opacity-50"
          data-testid="payout-verify-resend"
        >
          {resendBusy ? "Sending…" : "Resend code"}
        </button>
        <span className="text-[var(--muted)]/60">·</span>
        <button
          type="button"
          onClick={cancel}
          disabled={busy}
          className="underline text-[var(--muted)] hover:text-red-700"
          data-testid="payout-verify-cancel"
        >
          Discard change
        </button>
      </div>
    </div>
  );
}

